import type { ReactNode } from 'react';
import { LINE_TAB, LINE_TAB_OFF, LINE_TAB_ON, LINE_TAB_UNDERLINE, LINE_TABS_ROW } from '../ui/line-tab';
import { cn } from '@/lib/utils';

/**
 * 머리의 최상위 메뉴 — 홈 | 지원사업 | 아티클 | 커뮤니티 | 마이페이지.
 *
 * 밑줄 탭(ui/line-tab)이다 — 둘째 층은 검정 알약. 상세 페이지에서 「← 전지적 좋소 시점」 같은
 * 되돌아가기 줄을 따로 두지 않고, 이 줄이 어디에 있는지를 말한다.
 *
 * 링크는 render prop 으로 받는다 — 쓰는 쪽(layout/header.tsx)이 라우터 <Link> 를 준다.
 */
export type TopNavKey = 'home' | 'news' | 'articles' | 'community' | 'me';

export const TOP_NAV: ReadonlyArray<{ key: TopNavKey; label: string; href: string }> = [
  { key: 'home', label: '홈', href: '/' },
  { key: 'news', label: '지원사업', href: '/news' },
  { key: 'articles', label: '아티클', href: '/articles' },
  { key: 'community', label: '커뮤니티', href: '/posts' },
  { key: 'me', label: '마이페이지', href: '/mypage' },
];

/** 경로로 알 수 있는 현재 메뉴. 기둥 목록(/news/grants …)과 공고 상세(/news/:id)는 「지원사업」이다. */
export function activeTopNav(pathname: string): TopNavKey | null {
  const p = pathname.replace(/\/$/, '') || '/';
  if (p === '/') return 'home';
  if (/^\/news(\/|$)/.test(p)) return 'news';
  for (const item of TOP_NAV) {
    if (item.key !== 'home' && (p === item.href || p.startsWith(`${item.href}/`))) return item.key;
  }
  return null;
}

export type TopNavLink = (props: { href: string; className: string; children: ReactNode }) => ReactNode;
/** 켜진 탭의 밑줄. SSR 은 그냥 span, SPA 는 `motion.span layoutId` 로 미끄러지는 span. */
export type TopNavUnderline = (props: { className: string }) => ReactNode;
const staticUnderline: TopNavUnderline = ({ className }) => <span aria-hidden className={className} />;

export function TopNav({
  active,
  link,
  underline = staticUnderline,
}: {
  active: TopNavKey | null;
  link: TopNavLink;
  underline?: TopNavUnderline;
}) {
  // 위 8px(pt-2) — 로고 줄과 탭 사이 숨. sm 이상 머리 높이 = 48 + 8 + 33(탭 32 + 바닥선 1) = 89px.
  // 모바일은 하단 탭이 같은 다섯 항목을 맡으므로 이 줄이 없다 — 머리 48px.
  return (
    <nav aria-label="주요 메뉴" className="hidden pt-2 sm:block">
      <ul className={LINE_TABS_ROW}>
        {TOP_NAV.map((item) => {
          const on = item.key === active;
          return (
            <li key={item.key}>
              {link({
                href: item.href,
                className: cn(LINE_TAB, on ? LINE_TAB_ON : LINE_TAB_OFF),
                children: (
                  <>
                    {item.label}
                    {on ? underline({ className: LINE_TAB_UNDERLINE }) : null}
                  </>
                ),
              })}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
